/**
 * Page Content CMS — universal hook for admin-editable page headers.
 *
 * Stored server-side under settings key `page_header:{pageId}` with shape:
 *   {
 *     eyebrow:     string,   // small uppercase line above title
 *     title:       string,   // big display title
 *     subtitle:    string,   // western tagline below title
 *     body:        string,   // longer paragraph
 *     image:       string,   // hero/background image URL
 *     video:       string,   // hero video URL (mp4/uploaded)
 *     show_media:  bool,     // toggle media on/off
 *     overlay:     bool,     // dark overlay for readability
 *     cta_label:   string,   // optional CTA button label
 *     cta_href:    string,   // optional CTA button URL
 *   }
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../api";

// Default site logo. Page-level overrides (`logo` field on each page-header doc) take precedence.
export const DEFAULT_HEADER_LOGO =
  "https://customer-assets.emergentagent.com/job_chibah-clubhouse/artifacts/0hrtljet_8119D883-F4B4-4BC7-BA58-BDE3584E796E.png";

export const PAGE_CONTENT_DEFAULTS = {
  eyebrow: "",
  title: "",
  subtitle: "",
  body: "",
  image: "",
  video: "",
  logo: "",
  show_media: true,
  overlay: true,
  cta_label: "",
  cta_href: "",
};

/**
 * Fetch the page-content overrides for a given page id, merged with built-in defaults.
 * Returns the merged object, ready to render. Re-fetches when pageId changes.
 */
export const usePageContent = (pageId, defaults = {}) => {
  const baseline = { ...PAGE_CONTENT_DEFAULTS, ...defaults };
  const [content, setContent] = useState(baseline);

  useEffect(() => {
    let active = true;
    api
      .get(`/settings/page_header:${pageId}`)
      .then((r) => {
        if (!active) return;
        const value = r.data?.value;
        if (value && typeof value === "object") {
          // Keep falsy strings (empty == clearing the field) but ignore null/undefined per-key
          const merged = { ...baseline };
          for (const [k, v] of Object.entries(value)) {
            if (v !== null && v !== undefined && v !== "") merged[k] = v;
            else if (k in baseline && v === "") merged[k] = ""; // explicit empty wins
          }
          setContent(merged);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  return content;
};

export const resolveMediaUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return optimizeCloudinaryUrl(url);
};

/**
 * Inject Cloudinary delivery-time optimizations (q_auto, f_auto) so we serve
 * the smallest acceptable file format/quality per device. q_auto picks an
 * optimal quality based on the image content + viewer's connection;
 * f_auto picks the best format (WebP/AVIF where supported, JPEG/PNG fallback).
 * No-op for non-Cloudinary URLs or URLs that already have a transformation
 * block applied.
 */
export const optimizeCloudinaryUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("res.cloudinary.com")) return url;
  // Already optimized (or has any transformation chain right after /upload/) — leave alone.
  if (/\/upload\/(?:[a-z]_[^/]+,?)+\//i.test(url)) return url;
  return url.replace("/upload/", "/upload/q_auto,f_auto/");
};

/**
 * Map a pathname to the PAGES_CATALOG entry whose route best matches.
 * Used by the Header to find the right per-page logo override.
 *  - "/" only matches the home entry exactly.
 *  - "/crew/dixie" matches the "crew" entry (longest non-root prefix wins).
 *  - Section-level catalog entries (e.g. "prospects", "hang-arounds") that share
 *    a path with a page-level entry are skipped — first match (page-level) wins.
 */
const matchPageForPath = (pathname) => {
  // Exact-home short-circuit
  if (pathname === "/" || pathname === "") {
    return PAGES_CATALOG.find((p) => p.id === "home") || null;
  }
  const seenPaths = new Set();
  let best = null;
  let bestLen = 0;
  for (const entry of PAGES_CATALOG) {
    if (entry.path === "/" || seenPaths.has(entry.path)) {
      seenPaths.add(entry.path);
      continue;
    }
    seenPaths.add(entry.path);
    const ep = entry.path.replace(/\/$/, "");
    if (pathname === ep || pathname.startsWith(ep + "/")) {
      if (ep.length > bestLen) {
        best = entry;
        bestLen = ep.length;
      }
    }
  }
  return best;
};

/**
 * Default copy for the rotating top-of-page marquee. Editable per-site via the
 * `site_marquee_messages` setting (array of strings). Empty string in the list is skipped.
 */
export const DEFAULT_MARQUEE_MESSAGES = [
  "Established under suspicious circumstances",
  "Free shipping on orders of bad decisions",
  "The dog house is open",
  "Skate something you shouldn't",
  "Made in the desert, mailed from the moon",
];

/** Hook to read the live marquee message list. Falls back to defaults when no override saved. */
export const useMarqueeMessages = () => {
  const [list, setList] = useState(DEFAULT_MARQUEE_MESSAGES);
  /* eslint-disable */
  useEffect(() => {
    let active = true;
    api
      .get("/settings/site_marquee_messages")
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        if (Array.isArray(v)) {
          const cleaned = v.map((s) => String(s || "").trim()).filter(Boolean);
          if (cleaned.length) setList(cleaned);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  /* eslint-enable */
  return list;
};

/**
 * Returns the logo URL to render in the global Header for the current route.
 * Precedence:
 *   1. Per-page logo override (`page_header:{pageId}.logo`) — set via Admin → Pages
 *   2. Site-wide logo (`site_header_logo` setting) — set via Admin → Site Settings
 *   3. DEFAULT_HEADER_LOGO (built-in Chibah barbed-wire mark)
 * Also returns `isCustom` so the Header can choose whether to apply mixBlendMode.
 */
export const useHeaderLogo = () => {
  const { pathname } = useLocation();
  const matched = matchPageForPath(pathname);
  const pageId = matched?.id || "home";
  const [logo, setLogo] = useState("");
  const [siteLogo, setSiteLogo] = useState("");

  /* eslint-disable */
  // Site-wide logo: fetched once. Re-renders the header on change.
  useEffect(() => {
    let active = true;
    api
      .get("/settings/site_header_logo")
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        if (typeof v === "string" && v.trim()) setSiteLogo(v.trim());
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Per-page override: refetched whenever the route changes.
  useEffect(() => {
    let active = true;
    setLogo(""); // reset on route change so we don't flash the previous page's logo
    api
      .get(`/settings/page_header:${pageId}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        if (v && typeof v === "object" && typeof v.logo === "string" && v.logo) {
          setLogo(v.logo);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [pageId]);
  /* eslint-enable */

  const customLogo = logo || siteLogo;
  const url = customLogo ? resolveMediaUrl(customLogo) : DEFAULT_HEADER_LOGO;
  return { url, isCustom: !!customLogo };
};

/**
 * Catalog of editable page IDs the Admin → Pages tab will surface.
 * Defaults here match the current hard-coded copy so opening the admin form
 * pre-populates exactly what's on the live page.
 */
export const PAGES_CATALOG = [
  {
    id: "home",
    label: "Home / Dog House — Intro",
    path: "/",
    defaults: {
      eyebrow: "The Mission, Such As It Is",
      title: "Chibah Skateboards.\nCelebrating the best of our poor decisions since 2026.",
      subtitle: "Established under suspicious circumstances, we're here to document the trip.",
      body:
        "Catch up. Keep up. Cop some shit to help keep the lights on.\n\n" +
        "If our decks don't make you ollie higher or our shirts don't get you laid, we can't help you.",
    },
  },
  {
    id: "shop",
    label: "Shop — Spend Some Money",
    path: "/shop",
    defaults: {
      eyebrow: "The General Store",
      title: "Spend Some Money",
      subtitle: "",
      body: "Decks, hats, prints, and the occasional limited run. Each item handled by at least one of us with reasonably clean hands.",
    },
  },
  {
    id: "stories",
    label: "Stories / Articles",
    path: "/stories",
    defaults: {
      eyebrow: "Filed From The Road",
      title: "Stories",
      subtitle: "",
      body: "Field reports, open letters, and essays. Filed from gas stations, motel rooms, and the bed of a 1994 Chevy.",
    },
  },
  {
    id: "evidence",
    label: "Evidence",
    path: "/evidence",
    defaults: {
      eyebrow: "Submitted Into The Record",
      title: "Evidence",
      subtitle: "",
      body: "Photographs, clips, dispatches. The Chibah visual record.",
    },
  },
  {
    id: "crew",
    label: "The Crew — page header",
    path: "/crew",
    defaults: {
      eyebrow: "By order of the Marshal",
      title: "The Crew",
      subtitle: "",
      body: "Six humans of varying judgment.\nBounties listed in dollars, beverages, or sentimental value.",
    },
  },
  {
    id: "prospects",
    label: "Prospects — section header",
    path: "/crew",
    defaults: { eyebrow: "On Trial", title: "Prospects", subtitle: "", body: "" },
  },
  {
    id: "hang-arounds",
    label: "Hang Arounds — section header",
    path: "/crew",
    defaults: { eyebrow: "Loitering", title: "Hang Arounds", subtitle: "", body: "" },
  },
  {
    id: "bad-ideas",
    label: "Bad Ideas",
    path: "/bad-ideas",
    defaults: {
      eyebrow: "Bureau of Bad Ideas",
      title: "Bad Ideas",
      subtitle: "",
      body: "Filed under things we will probably regret. Rated by risk. Approved by no one.",
    },
  },
  {
    id: "about",
    label: "About / What The Hell Is Chibah",
    path: "/what-the-hell",
    defaults: {
      eyebrow: "A Pamphlet Of Partial Honesty",
      title: "What The Hell Is Chibah?",
      subtitle: "",
      body: "A skateboarding company, a clubhouse, a P.O. box, and a complaint department for the road.",
    },
  },
  {
    id: "complain",
    label: "Complain / Contact",
    path: "/complain",
    defaults: {
      eyebrow: "Filing Counter",
      title: "Complain Here",
      subtitle: "",
      body: "Story submissions, event ideas, photo dumps, and general grievances. Pick a category. Tell us your name. Don't lie.",
    },
  },
];
